import React from 'react';
import {
    ComingSoonCommercialTermsTableData,
    CommingSoonCommercialTermsRow,
} from '../RequestPreOrderEngagementAgreementModal/Models/Quote';
import Helpers from '../../../utils/helper';
import InfoTooltip from '../../../components/info-tooltip/info-tooltip';
import { FeeRefundStatuses } from '../../../slices/marketplace/pricing/models/fee-refund-status.enum';
import './ComingSoonCommercialTermsTable.scss';
import { CurrencyFormatter } from '../../../utils/currencyFormatter';

export interface FreemiumFeeRanges {
    transactionFeeMin: number;
    transactionFeeMax: number;
    processFeeMin: number;
    processFeeMax: number;
    tailPeriod: string;
}

interface ComingSoonCommercialTermsTableProps {
    data: ComingSoonCommercialTermsTableData;
    hasPaidSubscription?: boolean;
    freemiumFeeRanges?: FreemiumFeeRanges;
    transactionFeeTooltip?: string;
}
export const ComingSoonCommercialTermsTable = (
    props: ComingSoonCommercialTermsTableProps
) => {
    const mapLabelWithTooltip = (
        label: string,
        tooltip: string,
        tooltipKey: string
    ) => {
        return (
            <>
                <span className="tooltip-wrapper">
                    {label.split('\n').map((sentence, sIndex, sArray) => (
                        <>
                            {sentence.split(' ').map((word, wIndex, wArray) => {
                                const isLastWord =
                                    wIndex === wArray.length - 1 &&
                                    sIndex === sArray.length - 1;
                                return isLastWord && tooltip ? (
                                    <span key={wIndex} className="tooltip-text">
                                        {word}{' '}
                                        <InfoTooltip
                                            text={tooltip}
                                            idText={`${tooltipKey}-${Date.now().toString()}`}
                                        />
                                    </span>
                                ) : (
                                    <React.Fragment key={wIndex}>
                                        {word}{' '}
                                    </React.Fragment>
                                );
                            })}
                            {sIndex < sArray.length - 1 ? <br /> : []}
                        </>
                    ))}
                </span>
            </>
        );
    };
    const mapFeeRow = (row: CommingSoonCommercialTermsRow, index: number) => {
        const refundableStatus =
            row.refundable !== undefined && row.refundable !== null
                ? FeeRefundStatuses.find((f) => f.id === row.refundable)
                : undefined;
        return (
            <tr>
                <td className="left-indentation">
                    {mapLabelWithTooltip(
                        row.label,
                        row.toolTip,
                        `row-${row.id}-label-${index}}`
                    )}
                </td>
                <td>
                    {mapLabelWithTooltip(
                        row.feeAmountValue,
                        row.feeAmountValueTooltip,
                        `row-${row.id}-fee-value-${index}}`
                    )}
                </td>
                <td>
                    {refundableStatus
                        ? mapLabelWithTooltip(
                              refundableStatus.name,
                              refundableStatus.tooltipText,
                              `row-${row.id}-refundable-${index}`
                          )
                        : 'NA'}
                </td>
                <td>
                    {row.creditable !== undefined && row.creditable !== null
                        ? row.creditable === 0
                            ? 'No'
                            : mapLabelWithTooltip(
                                  `${Helpers.GetCurrencyFormat(
                                      row.creditable,
                                      0,
                                      undefined,
                                      ''
                                  )}%`,
                                  'Creditable against the transaction fee',
                                  `row-${row.id}-creditable-${index}`
                              )
                        : 'NA'}
                </td>
            </tr>
        );
    };

    const renderTransactionFeeLabel = () => (
        <>
            Transaction Fee
            {props.transactionFeeTooltip && (
                <InfoTooltip
                    text={props.transactionFeeTooltip}
                    idText="commercial-terms-transaction-fee"
                />
            )}
        </>
    );

    const renderFreemiumTable = () => {
        if (!props.freemiumFeeRanges) return <div>NA</div>;

        const { transactionFeeMin, transactionFeeMax, processFeeMin, processFeeMax, tailPeriod } = props.freemiumFeeRanges;

        return (
            <div className="coming-soon-commercial-terms-table">
                <table className="table">
                    <tbody>
                        <tr className="bold-row">
                            <td className="fee-type-column">Fee Type</td>
                            <td>Fee Amount</td>
                        </tr>
                        <tr>
                            <td>{renderTransactionFeeLabel()}</td>
                            <td>
                                {CurrencyFormatter.formatToDollarValue(transactionFeeMin)} - {CurrencyFormatter.formatToDollarValue(transactionFeeMax)}
                            </td>
                        </tr>
                        <tr>
                            <td>Process Fee</td>
                            <td>
                                {CurrencyFormatter.formatToDollarValue(processFeeMin)} - {CurrencyFormatter.formatToDollarValue(processFeeMax)}
                            </td>
                        </tr>
                        <tr>
                            <td>Tail Period</td>
                            <td>{tailPeriod}</td>
                        </tr>
                    </tbody>
                </table>
            </div>
        );
    };

    const renderPaidTable = () => {
        return (
            <div className="coming-soon-commercial-terms-table">
                <table className="table">
                    <tbody>
                        <tr className="bold-row">
                            <td className="fee-type-column">Fee Type</td>
                            <td>Fee Amount</td>
                            <td>Refundable</td>
                            <td className="creditable-column">Creditable </td>
                        </tr>
                        <tr>
                            <td>{renderTransactionFeeLabel()}</td>
                            <td>
                                {Helpers.GetCurrencyFormatWithDynamicCents(
                                    props.data.transactionFee,
                                    3,
                                    undefined,
                                    ''
                                )}
                                %
                            </td>
                            <td className="no-inline-border"></td>
                            <td className="no-inline-border"></td>
                        </tr>
                        <tr className="bold-row">
                            <td className="no-right-border">Process Fees</td>
                        </tr>
                        {props.data.processFees
                            .filter((t) => t !== undefined && t !== null)
                            .map((row, index) => mapFeeRow(row, index))}
                        <tr className="bold-row">
                            <td className="no-right-border">Other Fees</td>
                        </tr>
                        {props.data.otherFees
                            .filter((t) => t !== undefined && t !== null)
                            .map((row, index) => mapFeeRow(row, index))}
                        <tr>
                            <td>Tail Period</td>
                            <td>{props.data.tailPeriod}</td>
                        </tr>
                    </tbody>
                </table>
            </div>
        );
    };

    if (!props.data) {
        return <div>NA</div>;
    }

    if (props.hasPaidSubscription === false && props.freemiumFeeRanges) {
        return renderFreemiumTable();
    }

    return renderPaidTable();
};
